/**
 * Global SWR Configuration
 *
 * This file contains the global SWR configuration that provides:
 * - Error retry logic with exponential backoff
 * - Cache revalidation strategy optimized for the CareCal use case
 * - Deduplication intervals to prevent excessive API calls
 * - Error handling integration with toast notifications
 * - Authentication error handling with automatic logout
 *
 * Requirements: 12.3, 12.4
 */

import type { SWRConfiguration } from 'swr';
import { api, ApiError, AuthenticationError } from './api';
import { useAuthStore } from '../store/auth-store';
import { toast } from 'sonner';
import { env } from './env';

function isPublicDataKey(key: string): boolean {
  const normalizedKey = key.startsWith('http') ? new URL(key).pathname : key;

  if (normalizedKey.startsWith('/api/v1/public/')) {
    return true;
  }

  // TODO: Remove when we migrate to v1
  // Legacy public caregiver endpoints that should not affect auth state.
  return /^\/api\/caregivers\/(?!me(?:\/|$))[^/]+(?:\/availability|\/booking-requests)?$/.test(
    normalizedKey
  );
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Global error handler for SWR
 * Handles authentication errors, network errors, and displays appropriate user feedback
 */
function handleSWRError(error: Error | ApiError | AuthenticationError, key: string) {
  // Handle authentication errors
  if (error instanceof AuthenticationError || (error as ApiError)?.statusCode === 401) {
    if (isPublicDataKey(key)) {
      return;
    }

    const { logout, hasHydrated, token } = useAuthStore.getState();

    // Don't logout if hydration hasn't completed yet - the 401 might be due to
    // the race condition where the API client doesn't have the token yet
    if (!hasHydrated) {
      return;
    }

    // Don't logout if we don't have a token - user is already logged out
    if (!token) {
      return;
    }

    logout();

    // Don't show toast for auth errors on public routes
    if (!key.includes('/api/caregivers/') || key.includes('/me/')) {
      toast.error('Session expired', {
        description: 'Please log in again to continue.',
      });
    }
    return;
  }

  // Handle network errors
  if (!navigator.onLine) {
    toast.error('Network error', {
      description: 'Please check your internet connection and try again.',
    });
    return;
  }

  // Handle API errors with status codes
  const apiError = error as ApiError;
  if (apiError.statusCode) {
    // Handle server errors (5xx)
    if (apiError.statusCode >= 500) {
      toast.error('Server error', {
        description: 'Something went wrong on our end. Please try again later.',
      });
      return;
    }

    // Handle client errors (4xx) - but not 401 (handled above) or 404 (expected for some routes)
    if (apiError.statusCode >= 400 && apiError.statusCode < 500 && apiError.statusCode !== 404) {
      toast.error('Request failed', {
        description: apiError.message || 'Please check your input and try again.',
      });
      return;
    }
  }

  // For 404 errors and other cases, don't show toast as they might be expected
  // (e.g., checking if a profile exists, invalid link codes, etc.)
}

/**
 * Retry logic with exponential backoff
 * Determines whether to retry a failed request based on error type and attempt count
 */
function shouldRetryOnError(error: Error | ApiError | AuthenticationError): boolean {
  // Don't retry on authentication errors
  if (error instanceof AuthenticationError || (error as ApiError)?.statusCode === 401) {
    return false;
  }

  const apiError = error as ApiError;
  if (apiError.statusCode) {
    // Don't retry on client errors (4xx) except for rate limiting (429)
    if (apiError.statusCode >= 400 && apiError.statusCode < 500 && apiError.statusCode !== 429) {
      return false;
    }
  }

  // Retry on network errors, server errors (5xx), and rate limiting (429)
  return true;
}

// ============================================================================
// SWR Configuration
// ============================================================================

/**
 * Global SWR configuration
 * Optimized for CareCal's use cases with appropriate caching and retry strategies
 */
export const swrConfig: SWRConfiguration = {
  // ============================================================================
  // Fetcher Function
  // ============================================================================

  /**
   * Default fetcher function that uses our API client
   * Automatically handles authentication headers and error formatting
   */
  fetcher: (url: string) => api.get(url),

  // ============================================================================
  // Error Handling & Retry Logic
  // ============================================================================

  /**
   * Global error handler
   * Processes errors and shows appropriate user feedback
   */
  onError: handleSWRError,

  /**
   * Determines whether to retry failed requests
   * Avoids retrying on client errors but retries on network/server issues
   */
  shouldRetryOnError,

  /**
   * Maximum number of retry attempts
   * Balances user experience with server load
   */
  errorRetryCount: 3,

  /**
   * Retry delay calculation with exponential backoff
   * Prevents overwhelming the server during outages
   */
  errorRetryInterval: 1000, // Base retry interval - SWR will handle exponential backoff internally

  // ============================================================================
  // Cache Revalidation Strategy
  // ============================================================================

  /**
   * Revalidate when window regains focus
   * Ensures users see fresh data when returning to the app
   * Disabled by default but can be enabled per-hook for critical data
   */
  revalidateOnFocus: false,

  /**
   * Revalidate when network reconnects
   * Ensures data is fresh after network interruptions
   */
  revalidateOnReconnect: true,

  /**
   * Revalidate if data is stale when component mounts
   * Provides good balance between performance and freshness
   */
  revalidateIfStale: true,

  /**
   * Revalidate on mount if data exists
   * Disabled to improve performance - rely on staleness check instead
   */
  revalidateOnMount: undefined,

  // ============================================================================
  // Deduplication & Performance
  // ============================================================================

  /**
   * Deduplication interval (2 seconds)
   * Prevents multiple identical requests within this timeframe
   * Balances performance with data freshness for user interactions
   */
  dedupingInterval: 2000,

  /**
   * Focus throttle interval (5 seconds)
   * Limits how often focus revalidation can occur
   * Prevents excessive requests when rapidly switching between tabs
   */
  focusThrottleInterval: 5000,

  /**
   * Loading timeout
   * Prevents requests from hanging indefinitely
   * Uses environment-specific timeout configuration
   */
  loadingTimeout: env.prod.apiTimeout,

  // ============================================================================
  // Background Updates
  // ============================================================================

  /**
   * Refresh interval
   * Disabled by default - individual hooks can enable as needed
   * (e.g., availability data might refresh every 5 minutes)
   */
  refreshInterval: 0,

  /**
   * Refresh when window is hidden
   * Disabled to save bandwidth and battery on mobile devices
   */
  refreshWhenHidden: false,

  /**
   * Refresh when browser is offline
   * Disabled as it would fail anyway
   */
  refreshWhenOffline: false,

  // ============================================================================
  // Suspense & Concurrent Features
  // ============================================================================

  /**
   * Suspense mode
   * Disabled for now - can be enabled when we add Suspense boundaries
   */
  suspense: false,

  /**
   * Keep previous data while revalidating
   * Provides better UX by showing stale data instead of loading states
   */
  keepPreviousData: true,

  // ============================================================================
  // Fallback Data
  // ============================================================================

  /**
   * Fallback data for all requests
   * Undefined by default - individual hooks provide their own fallbacks
   */
  fallbackData: undefined,

  // ============================================================================
  // Middleware & Transforms
  // ============================================================================

  /**
   * Use middleware for additional processing
   * Can be used for logging, analytics, etc.
   */
  use: [
    // Development logging middleware
    ...(env.features.enableDebugLogging
      ? [
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (useSWRNext: any) => (key: any, fetcher: any, config: any) => {
            const swr = useSWRNext(key, fetcher, config);

            // Log SWR operations when debug logging is enabled
            if (key && typeof key === 'string') {
              console.debug('SWR:', {
                key,
                isLoading: swr.isLoading,
                isValidating: swr.isValidating,
                error: swr.error,
                data: swr.data ? 'loaded' : 'no data',
              });
            }

            return swr;
          },
        ]
      : []),
  ],
};

// ============================================================================
// Specialized Configurations
// ============================================================================

/**
 * Configuration for real-time data that needs frequent updates
 * Used for availability, booking requests, etc.
 */
export const realtimeConfig: Partial<SWRConfiguration> = {
  ...swrConfig,
  revalidateOnFocus: true,
  refreshInterval: env.isDevelopment ? 10000 : 30000, // Faster refresh in development
  dedupingInterval: 5000, // 5 seconds
  focusThrottleInterval: 2000, // 2 seconds
};

/**
 * Configuration for static/profile data that changes infrequently
 * Used for user profiles, caregiver profiles, etc.
 */
export const staticConfig: Partial<SWRConfiguration> = {
  ...swrConfig,
  revalidateOnFocus: false,
  refreshInterval: 0,
  dedupingInterval: env.prod.cacheTimeout / 5, // 1 minute in production, shorter in dev
  focusThrottleInterval: 30000, // 30 seconds
};

/**
 * Configuration for public data that doesn't require authentication
 * Used for public caregiver profiles, availability, etc.
 */
export const publicConfig: Partial<SWRConfiguration> = {
  ...swrConfig,
  revalidateOnFocus: true,
  refreshInterval: env.prod.cacheTimeout, // Use environment cache timeout
  dedupingInterval: 30000, // 30 seconds
  errorRetryCount: 2, // Fewer retries for public data
  onError: (error: Error | ApiError | AuthenticationError, key: string) => {
    // Don't show auth error toasts for public routes
    if (error instanceof AuthenticationError || (error as ApiError)?.statusCode === 401) {
      return;
    }
    handleSWRError(error, key);
  },
};
